import type { BookingDocument } from '../../domain/types'
import { Icon, type IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import styles from './DocumentCard.module.css'

const TITLE_KEY = {
  invoiceRegular: 'booking_details_documents_invoice_regular',
  invoiceCancellation: 'booking_details_documents_invoice_cancellation',
  travelDocumentVouchers: 'booking_details_documents_travel_document_set_of_documents',
  travelDocumentCarRental: 'booking_details_documents_travel_document_car_rental',
  atolCertificate: 'booking_details_documents_atol_certificate',
  other: 'booking_details_documents_other',
} as const

function getDocumentTitleKey(
  type: BookingDocument['type'],
  subType: BookingDocument['subType'],
): string {
  switch (type) {
    case 'AtolCertificate':
      return TITLE_KEY.atolCertificate
    case 'Invoice':
      return subType === 'Cancellation' ? TITLE_KEY.invoiceCancellation : TITLE_KEY.invoiceRegular
    case 'TravelDocument':
      return subType === 'CarRental' ? TITLE_KEY.travelDocumentCarRental : TITLE_KEY.travelDocumentVouchers
    default:
      return TITLE_KEY.other
  }
}

function getDocumentGlyph(
  type: BookingDocument['type'],
  subType: BookingDocument['subType'],
): IconGlyph {
  switch (type) {
    case 'AtolCertificate':
      return 'Certificate'
    case 'Invoice':
      return 'DocumentWithEuro'
    case 'TravelDocument':
      return subType === 'CarRental' ? 'CarFront' : 'DocumentWithArrowDownToLine'
    default:
      return 'DocumentWithArrowDownToLine'
  }
}

interface DocumentCardProps {
  document: BookingDocument
}

export function DocumentCard({ document: { title, subtitle, url, type, subType } }: DocumentCardProps) {
  const { formatMessage } = useIntl()
  const glyph = getDocumentGlyph(type, subType)
  const displayTitle = title || formatMessage({ id: getDocumentTitleKey(type, subType) })

  return (
    <a href={url} className={styles.card} download rel="noreferrer">
      <Icon glyph={glyph} size={24} />
      <div className={styles.content}>
        <Typography variant="body-m-bold-flat">{displayTitle}</Typography>
        {subtitle && (
          <Typography className={styles.subtitle} variant="body-s-regular-tight">{subtitle}</Typography>
        )}
      </div>
      <Icon glyph="ArrowDownInCircle" size={24} />
    </a>
  )
}
